// src/utils/money.model.ts
var MONEY_DECIMALS = 1;
var ROUND_FACTOR = 10 ** MONEY_DECIMALS;
function roundMoney(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const scaled = value * ROUND_FACTOR;
  const roundedScaled = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return roundedScaled / ROUND_FACTOR;
}

// src/pos/ordering.model.ts
var QUICK_CHARGE_CODE_PREFIX = "QC_";
function isQuickChargeOrderCode(orderCode) {
  return orderCode?.startsWith(QUICK_CHARGE_CODE_PREFIX) ?? false;
}

// src/pos/cash-alterations.model.ts
import { z } from "zod";
var CASH_ALTERATION_ACTIONS = ["entry-cash", "in", "out"];
var cashAlterationActionSchema = z.enum(CASH_ALTERATION_ACTIONS);

// src/pos/tips.model.ts
function resolveTip(mode, value, base) {
  if (mode == null || value == null || value <= 0) {
    return 0;
  }
  if (mode === "value") {
    return roundMoney(value);
  }
  return roundMoney(base * value / 100);
}
function allocateGlobalTipShare(remainingTip, lineAmount, remainingItems, isClosingLine) {
  if (remainingTip <= 0) {
    return 0;
  }
  if (isClosingLine) {
    return roundMoney(Math.max(0, remainingTip));
  }
  if (remainingItems <= 0) {
    return 0;
  }
  return roundMoney(remainingTip * lineAmount / remainingItems);
}
function allocateProportionalTip(total, lineAmounts) {
  let remainingTip = total;
  let remainingItems = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  return lineAmounts.map((amount, index) => {
    const isClosingLine = index === lineAmounts.length - 1;
    const share = allocateGlobalTipShare(remainingTip, amount, remainingItems, isClosingLine);
    remainingTip = roundMoney(remainingTip - share);
    remainingItems = roundMoney(remainingItems - amount);
    return share;
  });
}
function isEqualSplit(lineAmounts) {
  if (lineAmounts.length < 2) {
    return true;
  }
  return roundMoney(Math.max(...lineAmounts) - Math.min(...lineAmounts)) <= 0.1;
}
function allocateEvenTotalTip(total, lineAmounts, itemsTotal) {
  const n = lineAmounts.length;
  const grandTenths = Math.round(roundMoney(itemsTotal + total) * 10);
  const baseTenths = Math.floor(grandTenths / n);
  const extra = grandTenths - baseTenths * n;
  const shares = lineAmounts.map((amount, index) => {
    const targetTenths = baseTenths + (index >= n - extra ? 1 : 0);
    return roundMoney(targetTenths / 10 - amount);
  });
  return shares.some((share) => share < 0) ? null : shares;
}
function allocateGlobalTip(globalTip, lineAmounts) {
  const total = roundMoney(globalTip);
  const itemsTotal = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  if (total <= 0 || itemsTotal <= 0) {
    return lineAmounts.map(() => 0);
  }
  if (isEqualSplit(lineAmounts)) {
    const evenTotal = allocateEvenTotalTip(total, lineAmounts, itemsTotal);
    if (evenTotal) {
      return evenTotal;
    }
  }
  return allocateProportionalTip(total, lineAmounts);
}

// src/pos/stream-events.model.ts
var POS_STREAM_EVENT_TYPES = [
  "order.opened",
  "order.upserted",
  "order.closed",
  "orderline.added",
  "orderline.updated",
  "orderline.item_payment_updated",
  "orderline.removed",
  "orderline.sent",
  "paymentline.created",
  "paymentline.done",
  "credit.tap.pending",
  "credit.tap.released",
  "credit.transaction.pending",
  "credit.transaction.resolved",
  "session.opened",
  "session.closed",
  "session.cash_alterations_updated",
  "cashDrawer.notConfigured",
  "session.daily_brief_updated",
  "session.auto_close_time_updated",
  "tenant.floor_background_updated",
  "area.upserted",
  "table.upserted",
  "table.removed",
  "employee.upserted",
  "employee.deactivated",
  "employee.kicked",
  "product.upserted",
  "product.deactivated",
  "payment-terminal.upserted",
  "payment-terminal.active-changed",
  "category.upserted",
  "printer.upserted",
  "happy_hour.changed",
  "variantComboGroup.upserted",
  "variantComboGroup.removed",
  "merge.links.changed",
  "approval_request.created",
  "approval_request.decided",
  "tenant.maintenance-mode-changed",
  "pos.refresh-requested",
  "employee.presence.probe.requested",
  "employee.presence.probe.acknowledged"
];

// src/pos/past-receipts.model.ts
import { z as z2 } from "zod";
var CLOSED_ORDERS_PAGE_SIZE_MAX = 50;
var CLOSED_ORDERS_PAGE_SIZE_DEFAULT = 30;
var SESSION_FILTER_OPTIONS_LIMIT = 200;
var closedOrdersQuerySchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  pageSize: z2.coerce.number().int().min(1).max(CLOSED_ORDERS_PAGE_SIZE_MAX).default(CLOSED_ORDERS_PAGE_SIZE_DEFAULT),
  dateFrom: z2.string().trim().min(1).optional(),
  dateTo: z2.string().trim().min(1).optional(),
  search: z2.string().trim().min(1).max(120).optional(),
  customer: z2.string().trim().min(1).max(120).optional(),
  sessionId: z2.coerce.number().int().min(1).optional()
});

// src/pos/accountant-report.model.ts
import { z as z3 } from "zod";
var accountantReportRangeSchema = z3.object({
  from: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/)
}).strict().refine((value) => value.from <= value.to, { message: "from must be on or before to", path: ["from"] });
var accountantReportSessionsSchema = z3.object({
  sessionIds: z3.array(z3.number().int().positive()).min(1)
}).strict();

export {
  MONEY_DECIMALS,
  roundMoney,
  QUICK_CHARGE_CODE_PREFIX,
  isQuickChargeOrderCode,
  CASH_ALTERATION_ACTIONS,
  cashAlterationActionSchema,
  resolveTip,
  allocateGlobalTipShare,
  allocateGlobalTip,
  POS_STREAM_EVENT_TYPES,
  CLOSED_ORDERS_PAGE_SIZE_MAX,
  CLOSED_ORDERS_PAGE_SIZE_DEFAULT,
  SESSION_FILTER_OPTIONS_LIMIT,
  closedOrdersQuerySchema,
  accountantReportRangeSchema,
  accountantReportSessionsSchema
};
